import SimpleTable from '@/components/common/tables/simpleTable';
import React from 'react';
import { ClientDirectoryHeader } from './ClientDirectoryHeader';
import {
	Avatar,
	DotStatus,
	EmptyUsers,
	FunnelEmptyScreen,
	Icon,
	Typography,
} from '@/components/common';
import Icons from '@/const/icons';
import { SegmentClient } from '../common';
import {
	CasesFilter,
	ClientNameFilter,
	MessagesFilter,
	SegmentFilter,
	TasksFilter,
	ClientTasksFilter,
} from './filters';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { GET_CLIENT_DIRECTORY } from '@/api';
import { clientDirectoryService } from '@/api/clientDirectory';
import {
	ActionStatus,
	ClientDirectoryI,
	MessageFilterType,
} from '@/interfaces';
import clsx from 'clsx';
import { useFilters } from '@/hooks';
import { DateRangeFilter } from '../../dashboard/table-filters/DateRangeFilter';

export const ClientDirectoryScreen: React.FC = () => {
	const LIMIT_PAGE = 40;
	const tableRef = React.useRef<HTMLDivElement | null>(null);
	const router = useRouter();
	const { lastFilter } = useFilters();
	const {
		page,
		search,
		clientId,
		isOrderAlph,
		actionStatusLawyer,
		actionStatusClient,
		caseId,
		messageFilter,
		segmentId,
		totalCase,
		totalTaskClient,
		totalTaskLawyer,
		totalMessage,
	} = router.query;

	const { data: clientDirectoryList, isLoading } = useQuery({
		queryKey: [
			GET_CLIENT_DIRECTORY,
			page,
			search,
			clientId,
			actionStatusLawyer,
			actionStatusClient,
			caseId,
			messageFilter,
			segmentId,
			isOrderAlph,
			totalCase,
			totalTaskClient,
			totalTaskLawyer,
			totalMessage,
			router,
		],
		queryFn: () =>
			clientDirectoryService.getClientDirectoryList(
				Number(page || '1'),
				LIMIT_PAGE,
				search ? encodeURIComponent(search as string) : undefined,
				clientId as string,
				segmentId
					? Array.isArray(segmentId)
						? segmentId
						: [segmentId]
					: undefined,
				!!isOrderAlph,
				caseId as string,
				totalCase ? Number(totalCase) : undefined,
				totalTaskClient ? Number(totalTaskClient) : undefined,
				totalTaskLawyer ? Number(totalTaskLawyer) : undefined,
				actionStatusLawyer?.toString()?.toUpperCase() as ActionStatus,
				actionStatusClient?.toString()?.toUpperCase() as ActionStatus,
				totalMessage ? Number(totalMessage) : undefined,
				messageFilter?.toString()?.toUpperCase() as MessageFilterType
			),
	});

	React.useEffect(() => {
		handleScrollTop();
	}, [page]);

	const handleScrollTop = () => {
		// Scrolling to the page top
		tableRef.current?.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	const onClickRow = (value: any) => {
		router.push(`/lawyerapp/client-profile/${value.id}`);
	};

	const lastFilterValue = lastFilter();

	const filteredClients = React.useMemo(() => {
		const { dateFrom, dateTo } = router.query;
		const directoryList =
			(clientDirectoryList?.data.items as ClientDirectoryI[]) || [];

		if (!dateFrom) {
			console.log('No dateFrom, returning all items');
			return directoryList;
		}

		const fromDate = dateFrom as string;
		const toDate = (dateTo as string) || fromDate;

		const filtered = directoryList.filter((client) => {
			if (!client.joined_date) {
				console.log('Client without joined_date:', client);
				return false;
			}

			// Use the EXACT SAME UTC logic as the table to format dates
			const date = new Date(client.joined_date);
			const year = date.getUTCFullYear();
			const month = String(date.getUTCMonth() + 1).padStart(2, '0');
			const day = String(date.getUTCDate()).padStart(2, '0');
			// const displayedDate = `${month}-${day}-${year}`;
			// Convert MM-dd-yyyy to yyyy-MM-dd for comparison
			const clientDateString = `${year}-${month}-${day}`;

			const isInRange =
				clientDateString >= fromDate && clientDateString <= toDate;
			return isInRange;
		});

		return filtered;
	}, [clientDirectoryList, router.query]);

	const getStatusColor = (
		status: string
	): 'green' | 'orange' | 'red' | 'grey' => {
		if (status === 'IN_PROGRESS') {
			return 'green';
		}

		if (status === 'DUE_SOON' || status === 'ON_HOLD') {
			return 'orange';
		}

		if (status === 'ACTION_REQUIRED' || status === 'PAST_DUE') {
			return 'red';
		}

		return 'grey';
	};

	const renderEmptyState = () => {
		return (
			<div className="pt-[88px]">
				{lastFilterValue === 'search' && (
					<div className={`text-center`}>
						<Icon
							src={Icons.searchCaseEmptyState}
							className="!w-[175px] !h-[140px] m-auto 3xl:!w-[200px] 3xl:!h-[160px]"
						/>
						<Typography type="regular-18" className="text-center">
							No results were found for your search.
						</Typography>
						<br />
						<Typography type="regular-18">
							Please try using different keywords.
						</Typography>
					</div>
				)}

				{lastFilterValue && lastFilterValue !== 'search' && (
					<FunnelEmptyScreen
						message="There are currently no clients matching this filter."
						classNameIcon="!w-[175px] !h-[140px] 3xl:!w-[200px] 3xl:!h-[160px]"
					/>
				)}

				{!lastFilterValue && (
					<EmptyUsers classNameIcon="!w-[175px] !h-[140px] mb-6" />
				)}
			</div>
		);
	};

	const getClientName = (instance: ClientDirectoryI): string => {
		if (instance.firstname) return `${instance.firstname} ${instance.lastname}`;

		return 'Unnamed';
	};

	return (
		<div
			className={clsx('bg-white w-full rounded-2xl p-4', {
				'h-[calc(700px-4rem)] 2xl:h-[calc(800px-4rem)]':
					!clientDirectoryList?.data || filteredClients.length === 0,
			})}
			ref={tableRef}
		>
			<ClientDirectoryHeader
				title="Client Database"
				showingItems={filteredClients.length || 0}
				totalItems={clientDirectoryList?.data.totalItems || 0}
				labelTotalItems="clients"
				allowDynamicUpdate={true}
			/>

			<SimpleTable<ClientDirectoryI>
				classNameContainer="border-none shadow-none mt-6 h-[60vh]"
				classNameHead="bg-white !opacity-100"
				classNameHeader="w-full lg:grid lg:grid-cols-[minmax(200px,1.7fr)_minmax(130px,0.7fr)_minmax(130px,0.7fr)_minmax(90px,0.7fr)_minmax(100px,0.8fr)_minmax(160px,1.3fr)_minmax(120px,0.9fr)] place-items-baseline"
				classNameRows="text-black lg:grid lg:grid-cols-[minmax(200px,1.7fr)_minmax(130px,0.7fr)_minmax(130px,0.7fr)_minmax(90px,0.7fr)_minmax(100px,0.8fr)_minmax(160px,1.3fr)_minmax(120px,0.9fr)] items-center cursor-pointer"
				hideRowActions
				tableEmptyComponent={renderEmptyState()}
				onClickRow={onClickRow}
				totalPages={clientDirectoryList?.data.totalPages}
				columns={[
					{
						header: <ClientNameFilter />,
						content: (instance) => (
							<div className="flex items-center gap-x-2">
								<Avatar
									photoUrl={
										!instance.firstname
											? Icons.avatar
											: instance.profile_picture || ''
									}
									size="small"
									firstname={instance.firstname}
									lastname={instance.lastname}
								/>
								{getClientName(instance)}
							</div>
						),
					},
					{
						header: (
							<TasksFilter
								title="Lawyer's Tasks"
								filterKey="actionStatusLawyer"
								totalFilterKey="totalTaskLawyer"
							/>
						),
						content: (instance) => (
							<div className="flex items-center  gap-x-2">
								<Typography type="regular-14" className="w-[55px]">
									{instance.lawyer_tasks} Tasks
								</Typography>
								<div className="flex gap-1 ">
									{instance.lawyer_action_status_array.map((status) => (
										<DotStatus key={status} color={getStatusColor(status)} />
									))}
								</div>
							</div>
						),
					},
					{
						header: (
							<ClientTasksFilter
								title="Client's Tasks"
								filterKey="actionStatusClient"
								totalFilterKey="totalTaskClient"
							/>
						),
						content: (instance) => (
							<div className="flex justify-start items-center  gap-x-2">
								<Typography type="regular-14" className="w-[55px]">
									{instance.client_tasks} Tasks
								</Typography>
								<div className="flex gap-1 ">
									{instance.client_action_status_array
										.filter((status) => status !== 'IN_PROGRESS')
										.map((status) => (
											<DotStatus key={status} color={getStatusColor(status)} />
										))}
								</div>
							</div>
						),
					},
					{
						header: <CasesFilter />,
						content: (instance) => (
							<div className="flex items-center gap-x-2">
								{instance.total_cases}{' '}
								<Icon
									src={Icons.briefcase}
									width={16}
									height={16}
									className="text-opacity-black"
								/>
							</div>
						),
					},
					{
						header: <MessagesFilter />,
						content: (instance) => (
							<div className="flex items-center gap-x-2">
								{instance.total_message}{' '}
								<span className="relative">
									<Icon
										src={Icons.sms}
										width={16}
										height={16}
										className="text-opacity-black relative"
									/>
									{instance.has_unread_messages ? (
										<span className="bg-main-red h-2 w-2 rounded-full absolute -top-0.5 -right-0.5 border border-white" />
									) : null}
								</span>
							</div>
						),
					},
					{
						header: <SegmentFilter />,
						content: (instance) =>
							instance.segmentation ? (
								<SegmentClient
									label={instance.segmentation}
									withTextColor
									// cutCustomer
								/>
							) : (
								<></>
							),
					},
					{
						header: <DateRangeFilter title="Joined Date" restrictToPast />,
						content: (instance) => {
							let formattedDate = '';
							if (instance.joined_date) {
								const date = new Date(instance.joined_date);
								const year = date.getUTCFullYear();
								const month = String(date.getUTCMonth() + 1).padStart(2, '0');
								const day = String(date.getUTCDate()).padStart(2, '0');
								formattedDate = `${month}-${day}-${year}`;
							}
							return (
								<div className="flex justify-between items-center gap-2">
									<Typography type="regular-14">{formattedDate}</Typography>
								</div>
							);
						},
					},
				]}
				rows={filteredClients}
				isLoading={isLoading}
			/>
		</div>
	);
};
